import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import blogCategoryApi from '../../api/blogCategoryApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditBlogCategory = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    is_active: 0,
  });
  const [categoryFound, setCategoryFound] = useState(true);

  useEffect(() => {
    const fetchCategory = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await blogCategoryApi.getBlogCategoryByIdentifier(id);
        if (response.success && response.data) {
          const categoryToEdit = response.data;
          setFormData({
            name: categoryToEdit.name,
            is_active: categoryToEdit.is_active ? 1 : 0,
          });
          setCategoryFound(true);
        } else {
          setCategoryFound(false);
          setGeneralError(response.message || 'Category not found.');
        }
      } catch (err) {
        console.error('Failed to fetch category:', err);
        setCategoryFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load category. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    try {
      await blogCategoryApi.updateBlogCategory(id, formData);
      addToast('Blog category updated successfully!', 'success');
      navigate(ROUTES.BLOG_CATEGORIES);
    } catch (err) {
      console.error('Failed to update blog category:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update blog category. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!categoryFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Category Not Found</h1>
        <p className="text-text-gray">{generalError || 'The blog category you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.BLOG_CATEGORIES)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Categories List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Blog Category</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Category Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Category Name"
          error={validationErrors?.name?.[0]}
        />
        <CheckboxInput
          label="Active"
          name="is_active"
          id="is_active"
          checked={formData.is_active}
          onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Category
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.BLOG_CATEGORIES)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditBlogCategory;